import React, { createContext, useContext, useState } from "react";
import { LabTest, TestPackage } from "@/services/data";

interface CartItem {
  type: "test" | "package";
  item: LabTest | TestPackage;
}

interface CartContextType {
  items: CartItem[];
  addTest: (test: LabTest) => void;
  addPackage: (pkg: TestPackage) => void;
  removeItem: (id: string) => void;
  clearCart: () => void;
  total: number;
  itemCount: number;
  hasItem: (id: string) => boolean;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [items, setItems] = useState<CartItem[]>([]);

  const addTest = (test: LabTest) => {
    if (items.find(i => i.item.id === test.id)) return;
    setItems(prev => [...prev, { type: "test", item: test }]);
    console.log("Added test to cart:", test.name);
  };

  const addPackage = (pkg: TestPackage) => {
    if (items.find(i => i.item.id === pkg.id)) return;
    setItems(prev => [...prev, { type: "package", item: pkg }]);
    console.log("Added package to cart:", pkg.name);
  };

  const removeItem = (id: string) => {
    setItems(prev => prev.filter(i => i.item.id !== id));
  };

  const clearCart = () => setItems([]);

  const total = items.reduce((sum, i) => sum + i.item.discountedPrice, 0);
  const itemCount = items.length;
  const hasItem = (id: string) => items.some(i => i.item.id === id);

  return (
    <CartContext.Provider value={{ items, addTest, addPackage, removeItem, clearCart, total, itemCount, hasItem }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
};
